import React, { useCallback, useMemo } from 'react';
import { type CheckType } from 'schema-typed';
import { AddressOption } from 'src/types/address';
import AnnotatedCheckbox from '../../AnnotatedCheckbox';
import {
  CityRestrictedAddressLookup,
  UnrestrictedAddressLookup,
} from '../../AddressLookup';

export interface AddressFieldProps {
  autoComplete?: string;
  checkboxLabel?: string;
  checkTrigger?: 'change' | 'blur' | 'none';
  disabled?: boolean;
  errorMessage?: string;
  isChecked?: boolean;
  label: string;
  name: string;
  onCheckboxChange?: (checked: boolean) => void;
  restrictAddressToCity?: boolean;
  showCheckbox?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationRule?: CheckType<unknown, any>;
  value?: string | AddressOption;
}

export const AddressField = ({
  checkboxLabel,
  disabled = false,
  isChecked = false,
  onCheckboxChange,
  restrictAddressToCity = false,
  showCheckbox = false,
  value,
  errorMessage,
  ...sharedProps
}: AddressFieldProps) => {
  const inputDisabled = useMemo(
    () => disabled || isChecked,
    [disabled, isChecked]
  );

  const renderAddressLookup = useCallback(() => {
    if (restrictAddressToCity) {
      return (
        <CityRestrictedAddressLookup
          {...sharedProps}
          disabled={inputDisabled}
          value={value ?? ''}
        />
      );
    }
    return (
      <UnrestrictedAddressLookup
        {...sharedProps}
        disabled={inputDisabled}
        value={value ?? ''}
      />
    );
  }, [inputDisabled, restrictAddressToCity, sharedProps, value]);

  return (
    <div
      className={showCheckbox ? '' : 'mb-12'}
      data-cy={`person-${sharedProps.name}`}
    >
      {renderAddressLookup()}

      {errorMessage && (
        <div
          style={{ marginTop: '4px' }}
          className="text-error"
        >
          {errorMessage}
        </div>
      )}
      {showCheckbox && checkboxLabel && (
        <div className="mb-12">
          <AnnotatedCheckbox
            disabled={disabled}
            label={checkboxLabel}
            name={`checkboxsameas-${sharedProps.name}`}
            onChange={onCheckboxChange}
            value={isChecked}
          />
        </div>
      )}
    </div>
  );
};
